import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import useUrlState from "@/hooks/useUrlState";

import { AdminStatGrid } from "@/components/admin/AdminStatGrid";
import { ServicesStatusStrip } from "@/components/admin/ComplianceShortcut";
import PageHeader from "@/components/dashboard/PageHeader";
import { SubscriptionRequestsTab } from "@/components/admin/SubscriptionRequestsTab";
import { UsersTab } from "@/components/admin/HistoryUsersTabs";
import { ModerationTab } from "@/components/admin/ModerationTab";
import { CronBillingTab } from "@/components/admin/CronBillingTab";
import AdminBroadcastCard from "@/components/admin/AdminBroadcastCard";
import UatTelemetryCard from "@/components/admin/UatTelemetryCard";
import SuperAdminAuditLogCard from "@/components/admin/SuperAdminAuditLogCard";
import AdminSearchAnalyticsCard from "@/components/admin/AdminSearchAnalyticsCard";
import TraderOfWeekCard from "@/components/admin/TraderOfWeekCard";
import FinanceDashboard from "@/components/admin/FinanceDashboard";
import DeploymentEventsCard from "@/components/admin/DeploymentEventsCard";
import SuperAdminAgenciesCard from "@/components/admin/SuperAdminAgenciesCard";
import FeedbackInboxCard from "@/components/admin/FeedbackInboxCard";
import AdminDataHygieneCard from "@/components/admin/AdminDataHygieneCard";
import ConsistencySelfTestCard from "@/components/admin/ConsistencySelfTestCard";
import AdminSessionsCard from "@/components/admin/AdminSessionsCard";
import AdminExportsCard from "@/components/admin/AdminExportsCard";
import AdminObondoCard from "@/components/admin/AdminObondoCard";
import FactoryResetCard from "@/components/admin/FactoryResetCard";
import TrialAnalyticsCard from "@/components/admin/TrialAnalyticsCard";
import AdminPulseStrip from "@/components/admin/AdminPulseStrip";
import DashboardGridWrapper from "@/components/dashboard/DashboardGridWrapper";
import AdminQuotesCard from "@/components/admin/AdminQuotesCard";
import AdminLtiCard from "@/components/admin/AdminLtiCard";
import AdminMarketFeedsCard from "@/components/admin/AdminMarketFeedsCard";
import AdminActivityLogFeed from "@/components/admin/AdminActivityLogFeed";
import AdminMauBillingCard from "@/components/admin/AdminMauBillingCard";

export default function Admin() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [summary, setSummary] = useState(null);
    const [users, setUsers] = useState([]);
    const [cronStats, setCronStats] = useState(null);
    const [services, setServices] = useState(null);
    const [reports, setReports] = useState([]);
    const [flagged, setFlagged] = useState([]);
    const [busy, setBusy] = useState(false);

    async function load() {
        // Both `/admin/payouts/*` endpoints were retired Feb 2026 alongside the
        // retail payout flow. `/admin/analytics/refer-variants` (A/B test) was
        // retired with the A/B tab in the same sweep. The load pipeline is now
        // trimmed to the 5 endpoints still in use by this dashboard.
        const [{ data: s }, { data: u }, { data: sv }, { data: rp }, { data: fl }] = await Promise.all([
            api.get("/admin/summary"),
            api.get("/admin/users"),
            api.get("/admin/services"),
            api.get("/admin/community/reports"),
            api.get("/admin/community/flagged"),
        ]);
        setSummary(s); setUsers(u); setServices(sv);
        setReports(rp); setFlagged(fl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, []);

    async function moderate(postId, action) {
        setBusy(true);
        try {
            await api.post(`/admin/community/posts/${postId}/moderate`, { action });
            const past = action === "hide" ? "hidden" : action === "delete" ? "deleted" : "approved";
            toast.success(`Post ${past}`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        } finally { setBusy(false); }
    }

    async function runCron() {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/cron/daily-billing");
            setCronStats(data);
            // Cron responses may still carry legacy counters — surface what's meaningful.
            const trial = data.trial_downgraded ?? 0;
            const swept = data.processed ?? data.credits_consumed ?? 0;
            toast.success(`Cron run: ${swept} accounts swept · ${trial} trial→expired`);
            await load();
        } catch (e) { toast.error("Cron failed"); } finally { setBusy(false); }
    }

    // NOTE: The `approve(uid, crit)` helper that called POST /admin/payouts/approve
    // was removed Feb 2026 — the retail payout endpoint is retired. Institutional
    // subscription approvals now live in SubscriptionRequestsTab.jsx and hit
    // /admin/subscription-requests/{id}/approve.

    // Programmatic tab-switching — driven by the `md-admin-open-tab` window
    // event dispatched from the payments-mode pill (and future admin shortcuts).
    // Tab identity is persisted in the URL (?tab=…) so admins can bookmark
    // and share specific sub-views.
    const [activeTab, setActiveTab] = useUrlState("tab", "subscription-requests");
    useEffect(() => {
        const onOpen = (e) => { if (e?.detail) setActiveTab(e.detail); };
        window.addEventListener("md-admin-open-tab", onOpen);
        return () => window.removeEventListener("md-admin-open-tab", onOpen);
    }, [setActiveTab]);

    return (
        <>
            <PageHeader
                icon={ShieldCheck}
                eyebrow="Super Admin"
                title="Platform Control Center"
                subtitle="Users, agencies, moderation, finance, cron jobs, and system health at a glance."
            />

            <DashboardGridWrapper
                role="SUPER_ADMIN"
                viewId="admin-home"
                widgets={[
                    { id: "admin-pulse", title: "Platform Pulse", category: "Health", layout: { x: 0, y: 0, w: 12, h: 3 }, minW: 6, minH: 2, noPadding: true, render: () => <AdminPulseStrip /> },
                    { id: "admin-kpis", title: "Platform KPIs", category: "KPIs", layout: { x: 0, y: 3, w: 12, h: 6 }, minW: 6, minH: 3, noPadding: true, render: () => <AdminStatGrid summary={summary} /> },
                    { id: "admin-services", title: "Services Status", category: "Health", layout: { x: 0, y: 9, w: 12, h: 3 }, minW: 4, minH: 3, noPadding: true, render: () => <ServicesStatusStrip services={services} /> },
                ]}
            />

            <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
                <TabsList className="w-full justify-start border-b border-border bg-transparent p-0 rounded-none h-auto overflow-x-auto flex-nowrap gap-0">
                    {[
                        { value: "subscription-requests", label: "Institutional Upgrades" },
                        { value: "trial-analytics",   label: "Trial Funnel" },
                        { value: "uat-telemetry",     label: "UAT Telemetry" },
                        { value: "audit-log",         label: "Audit Log" },
                        { value: "users",             label: "Users" },
                        { value: "agencies",          label: "Institutions" },
                        { value: "moderation",        label: "Moderation" },
                        { value: "finance",           label: "Finance" },
                        { value: "cron",              label: "Cron / Billing" },
                        { value: "broadcast",         label: "Broadcast" },
                        { value: "trader-of-week",    label: "Trader of Week" },
                        { value: "deployment",        label: "Deployments" },
                        { value: "search-analytics",  label: "Search" },
                        { value: "feedback",          label: "Feedback" },
                        { value: "obondo-admin",      label: "Obondo" },
                        { value: "exports",           label: "Exports" },
                        { value: "sessions",          label: "Sessions" },
                        { value: "hygiene",           label: "Hygiene" },
                        { value: "selftest",          label: "Self-test" },
                        { value: "quotes",            label: "Quotes CMS" },
                        { value: "lti",               label: "LTI 1.3" },
                        { value: "market-feeds",      label: "Market Feeds" },
                        { value: "url-activity",       label: "URL Activity" },
                        { value: "mau-billing",        label: "API Billing" },
                        { value: "danger-zone",       label: "⚠ Danger Zone" },
                    ].map((t) => (
                        <TabsTrigger
                            key={t.value}
                            value={t.value}
                            data-testid={`admin-tab-${t.value}`}
                            className={
                                t.value === "danger-zone"
                                    ? "data-[state=active]:border-b-2 data-[state=active]:border-destructive data-[state=active]:shadow-none data-[state=active]:text-destructive rounded-none py-3 px-4 bg-transparent whitespace-nowrap text-sm font-semibold text-destructive/80 hover:text-destructive"
                                    : "data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none rounded-none py-3 px-4 bg-transparent whitespace-nowrap text-sm"
                            }
                        >
                            {t.label}
                        </TabsTrigger>
                    ))}
                </TabsList>

                <div className="mt-4">
                    <TabsContent value="subscription-requests">
                        <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-tab-subscription-requests" widgets={[
                            { id: "subscription-requests", title: "Institutional Upgrades", category: "Tools", layout: { x: 0, y: 0, w: 12, h: 16 }, minW: 6, minH: 6, render: () => <SubscriptionRequestsTab /> },
                        ]} />
                    </TabsContent>
                    <TabsContent value="trial-analytics">
                        <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-tab-trial-analytics" widgets={[
                            { id: "trial-analytics", title: "Trial Funnel", category: "Tools", layout: { x: 0, y: 0, w: 12, h: 16 }, minW: 6, minH: 6, render: () => <TrialAnalyticsCard /> },
                        ]} />
                    </TabsContent>
                    <TabsContent value="uat-telemetry">
                        <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-tab-uat-telemetry" widgets={[
                            { id: "uat-telemetry", title: "UAT Telemetry", category: "Tools", layout: { x: 0, y: 0, w: 12, h: 16 }, minW: 6, minH: 6, render: () => <UatTelemetryCard /> },
                        ]} />
                    </TabsContent>
                    <TabsContent value="audit-log">
                        <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-tab-audit-log" widgets={[
                            { id: "audit-log", title: "Audit Log", category: "Tools", layout: { x: 0, y: 0, w: 12, h: 16 }, minW: 6, minH: 6, render: () => <SuperAdminAuditLogCard /> },
                        ]} />
                    </TabsContent>
                    <TabsContent value="users">
                        <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-tab-users" widgets={[
                            { id: "users-tab", title: "Users", category: "Tools", layout: { x: 0, y: 0, w: 12, h: 16 }, minW: 6, minH: 6, render: () => <UsersTab users={users} onReload={load} /> },
                        ]} />
                    </TabsContent>
                    <TabsContent value="moderation">
                        <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-tab-moderation" widgets={[
                            { id: "moderation-tab", title: "Moderation", category: "Tools", layout: { x: 0, y: 0, w: 12, h: 16 }, minW: 6, minH: 6, render: () => <ModerationTab reports={reports} flagged={flagged} busy={busy} onModerate={moderate} /> },
                        ]} />
                    </TabsContent>
                    <TabsContent value="cron">
                        <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-tab-cron" widgets={[
                            { id: "cron-billing-tab", title: "Cron / Billing", category: "Tools", layout: { x: 0, y: 0, w: 12, h: 16 }, minW: 6, minH: 6, render: () => <CronBillingTab busy={busy} cronStats={cronStats} onRun={runCron} /> },
                        ]} />
                    </TabsContent>
                    <TabsContent value="broadcast">
                        <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-tab-broadcast" widgets={[
                            { id: "broadcast-card", title: "Broadcast", category: "Tools", layout: { x: 0, y: 0, w: 12, h: 16 }, minW: 6, minH: 6, render: () => <AdminBroadcastCard /> },
                        ]} />
                    </TabsContent>
                    <TabsContent value="trader-of-week">
                        <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-tab-trader-of-week" widgets={[
                            { id: "trader-of-week-card", title: "Trader of Week", category: "Tools", layout: { x: 0, y: 0, w: 12, h: 16 }, minW: 6, minH: 6, render: () => <TraderOfWeekCard /> },
                        ]} />
                    </TabsContent>
                    <TabsContent value="finance">
                        <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-tab-finance" widgets={[
                            { id: "finance-dashboard", title: "Finance", category: "Tools", layout: { x: 0, y: 0, w: 12, h: 16 }, minW: 6, minH: 6, render: () => <FinanceDashboard /> },
                        ]} />
                    </TabsContent>
                    <TabsContent value="deployment">
                        <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-tab-deployment" widgets={[
                            { id: "deployment-events-card", title: "Deployments", category: "Tools", layout: { x: 0, y: 0, w: 12, h: 16 }, minW: 6, minH: 6, render: () => <DeploymentEventsCard /> },
                        ]} />
                    </TabsContent>
                    <TabsContent value="search-analytics">
                        <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-tab-search-analytics" widgets={[
                            { id: "search-analytics-card", title: "Search", category: "Tools", layout: { x: 0, y: 0, w: 12, h: 16 }, minW: 6, minH: 6, render: () => <AdminSearchAnalyticsCard /> },
                        ]} />
                    </TabsContent>
                    <TabsContent value="agencies">
                        <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-tab-agencies" widgets={[
                            { id: "agencies-card", title: "Institutions", category: "Tools", layout: { x: 0, y: 0, w: 12, h: 16 }, minW: 6, minH: 6, render: () => <SuperAdminAgenciesCard /> },
                        ]} />
                    </TabsContent>
                    <TabsContent value="feedback">
                        <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-tab-feedback" widgets={[
                            { id: "feedback-inbox-card", title: "Feedback", category: "Tools", layout: { x: 0, y: 0, w: 12, h: 16 }, minW: 6, minH: 6, render: () => <FeedbackInboxCard /> },
                        ]} />
                    </TabsContent>
                    <TabsContent value="obondo-admin">
                        <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-tab-obondo-admin" widgets={[
                            { id: "obondo-admin-card", title: "Obondo", category: "Tools", layout: { x: 0, y: 0, w: 12, h: 16 }, minW: 6, minH: 6, render: () => <AdminObondoCard /> },
                        ]} />
                    </TabsContent>
                    <TabsContent value="exports">
                        <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-tab-exports" widgets={[
                            { id: "exports-card", title: "Exports", category: "Tools", layout: { x: 0, y: 0, w: 12, h: 16 }, minW: 6, minH: 6, render: () => <AdminExportsCard /> },
                        ]} />
                    </TabsContent>
                    <TabsContent value="sessions">
                        <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-tab-sessions" widgets={[
                            { id: "sessions-card", title: "Sessions", category: "Tools", layout: { x: 0, y: 0, w: 12, h: 16 }, minW: 6, minH: 6, render: () => <AdminSessionsCard /> },
                        ]} />
                    </TabsContent>
                    <TabsContent value="hygiene">
                        <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-tab-hygiene" widgets={[
                            { id: "hygiene-card", title: "Hygiene", category: "Tools", layout: { x: 0, y: 0, w: 12, h: 16 }, minW: 6, minH: 6, render: () => <AdminDataHygieneCard /> },
                        ]} />
                    </TabsContent>
                    <TabsContent value="selftest">
                        <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-tab-selftest" widgets={[
                            { id: "selftest-card", title: "Self-test", category: "Tools", layout: { x: 0, y: 0, w: 12, h: 16 }, minW: 6, minH: 6, render: () => <ConsistencySelfTestCard /> },
                        ]} />
                    </TabsContent>
                    <TabsContent value="quotes">
                        <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-tab-quotes" widgets={[
                            { id: "quotes-card", title: "Quotes CMS", category: "Tools", layout: { x: 0, y: 0, w: 12, h: 16 }, minW: 6, minH: 6, render: () => <AdminQuotesCard /> },
                        ]} />
                    </TabsContent>
                    <TabsContent value="lti">
                        <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-tab-lti" widgets={[
                            { id: "lti-card", title: "LTI 1.3", category: "Tools", layout: { x: 0, y: 0, w: 12, h: 16 }, minW: 6, minH: 6, render: () => <AdminLtiCard /> },
                        ]} />
                    </TabsContent>
                    <TabsContent value="market-feeds">
                        <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-tab-market-feeds" widgets={[
                            { id: "market-feeds-card", title: "Market Feeds", category: "Tools", layout: { x: 0, y: 0, w: 12, h: 16 }, minW: 6, minH: 6, render: () => <AdminMarketFeedsCard /> },
                        ]} />
                    </TabsContent>
                    <TabsContent value="url-activity">
                        <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-tab-url-activity" widgets={[
                            { id: "url-activity-card", title: "URL Activity", category: "Tools", layout: { x: 0, y: 0, w: 12, h: 16 }, minW: 6, minH: 6, render: () => <AdminActivityLogFeed /> },
                        ]} />
                    </TabsContent>
                    <TabsContent value="mau-billing">
                        <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-tab-mau-billing" widgets={[
                            { id: "mau-billing-card", title: "API Billing", category: "Tools", layout: { x: 0, y: 0, w: 12, h: 30 }, minW: 6, minH: 6, render: () => <AdminMauBillingCard /> },
                        ]} />
                    </TabsContent>
                    <TabsContent value="danger-zone">
                        <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-tab-danger-zone" widgets={[
                            { id: "factory-reset-card", title: "Danger Zone", category: "Tools", layout: { x: 0, y: 0, w: 12, h: 16 }, minW: 6, minH: 6, render: () => <FactoryResetCard /> },
                        ]} />
                    </TabsContent>
                </div>
            </Tabs>
        </>
    );
}
